import { Helmet } from "react-helmet-async";
import { useLang } from "../i18n/LangContext";
import type { Lang } from "../types";

export const SITE_URL = "https://fishing-iceland.pages.dev";
export const SITE_NAME = "Fishing Iceland";
const OG_IMAGE = `${SITE_URL}/og-default.png`;

const LOCALES: Record<Lang, string> = {
  de: "de_CH",
  en: "en_US",
  is: "is_IS",
};

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  publishedAt?: string;
  updatedAt?: string;
  jsonLd?: object | object[];
  noindex?: boolean;
}

export function SEOHead({
  title,
  description,
  path,
  image,
  type = "website",
  publishedAt,
  updatedAt,
  jsonLd,
  noindex = false,
}: SEOHeadProps) {
  const { lang } = useLang();
  const canonical = `${SITE_URL}${path}`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} · ${SITE_NAME}`;
  const ogImage = image ?? OG_IMAGE;
  const ld = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={LOCALES[lang]} />
      <meta property="og:image" content={ogImage} />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {updatedAt && <meta property="article:modified_time" content={updatedAt} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
